import { type Precision } from '@dl/shared';

import { toDisplayDate } from './convert.js';
import { mod } from './leap.js';
import { monthsCycle } from './months.js';
import {
  resolveLocalized,
  type Calendar,
  type CanonicalDay,
  type DisplayDate,
  type Era,
  type FormatterRef,
  type LocalizedString,
} from './types.js';

/**
 * Locale-aware display formatting for `DisplayDate`.
 *
 * Two surface forms are exposed:
 *
 * - `formatDisplayDate` — returns a single string in the calendar's
 *   default pattern. Used for status pills, audit logs, and any plain
 *   read surface.
 * - `formatDisplayDateParts` — returns a `{ day, month, year, era }`
 *   record so the client can lay the fields out as separate tokens
 *   (semantic chips, accessible labels) without reparsing the string.
 *
 * Both helpers stay calendar-agnostic — the order of fields and the
 * separators are picked per calendar id for the two calendars Phase 1
 * ships (`gregorian`, `harptos`). Other calendars (loaded from third-
 * party packs in later phases) fall through to a generic `D MONTH, Y ERA`
 * layout.
 *
 * No `Intl.DateTimeFormat` here — the canonical-day engine is not
 * Gregorian-centric, so the wall-clock formatter wouldn't apply. Real-
 * world dates use `formatRealDatetimeRange` (separate module).
 */

export interface FormatDisplayDateOptions {
  /** Active locale for resolving `LocalizedString` labels. Defaults to `en`. */
  locale?: string;
  /** Default fallback locale. Defaults to `en`. */
  defaultLocale?: string;
  /**
   * Override the era affix. When unset the era's `abbreviation` is used
   * if present, else its localized `label`/`name`.
   */
  era?: 'abbreviation' | 'name';
}

export interface DisplayDateParts {
  day: string;
  month: string;
  year: string;
  era: string;
}

interface MonthRef {
  name: string;
  label: LocalizedString | undefined;
}
interface EraRef {
  name: string;
  label: LocalizedString | undefined;
  abbreviation: string | undefined;
}

function findMonth(calendar: Calendar, monthIndex: number): MonthRef {
  const m = monthsCycle(calendar).months[monthIndex - 1];
  if (!m) throw new Error(`month ${monthIndex} out of range`);
  return { name: m.name, label: m.label };
}

function findEra(calendar: Calendar, eraId: string): EraRef | null {
  for (const e of calendar.eras) {
    if (e.id === eraId) return { name: e.name, label: e.label, abbreviation: e.abbreviation };
  }
  return null;
}

function findEraFull(calendar: Calendar, eraId: string): Era | null {
  for (const e of calendar.eras) {
    if (e.id === eraId) return e;
  }
  return null;
}

/**
 * Resolve the display label for a year inside an era (#241). For a
 * `named_years` era the label is the cycling name
 * (`names[(year - year_one) mod cycle]`); for ordinary forward/backward
 * eras it is the plain ordinal. Returns `null` when the era is not a
 * `named_years` era so callers fall back to the ordinal.
 */
function resolveNamedYear(era: Era, year: number): string | null {
  const numbering = era.numbering;
  if (numbering === undefined || numbering.kind !== 'named_years') return null;
  const idx = mod(year - era.year_one, numbering.cycle);
  // The cycle period may exceed the number of declared names (a sparse
  // list); clamp into `names` with the same modulo so resolution is total.
  const name = numbering.names[mod(idx, numbering.names.length)];
  return name ?? null;
}

/**
 * Resolve a `named_years` display label back to its ordinal year within an
 * era (#241) — the parse side of {@link formatDisplayDateParts}. Returns
 * the FIRST ordinal year whose name matches `label` (names repeat, so a
 * bare name maps to its representative in the first cycle). Returns `null`
 * when the era is not a `named_years` era or the name is unknown.
 *
 * The resulting ordinal can be fed straight into a `DisplayDate.year` and
 * resolved to a canonical day via `toCanonicalDay`.
 */
export function parseYearLabel(calendar: Calendar, eraId: string, label: string): number | null {
  const era = findEraFull(calendar, eraId);
  if (era === null) return null;
  const numbering = era.numbering;
  if (numbering === undefined || numbering.kind !== 'named_years') return null;
  const idx = numbering.names.findIndex((n) => n === label);
  if (idx < 0) return null;
  // `year_one` carries name index 0; the i-th name is `year_one + i`.
  return era.year_one + idx;
}

/** Title-case a stable identifier (e.g. `"highharvestide"` → `"Highharvestide"`). */
function titleCase(s: string): string {
  return s
    .split(/[_\s]+/)
    .filter((w) => w.length > 0)
    .map((w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function resolveMonthLabel(calendar: Calendar, monthIndex: number, locale: string, defaultLocale: string): string {
  const m = findMonth(calendar, monthIndex);
  if (m.label !== undefined) {
    const r = resolveLocalized(m.label, locale, defaultLocale);
    if (r.length > 0) return r;
  }
  return titleCase(m.name);
}

function resolveEraAffix(
  calendar: Calendar,
  eraId: string,
  locale: string,
  defaultLocale: string,
  preference: 'abbreviation' | 'name',
): string {
  const e = findEra(calendar, eraId);
  if (e === null) return '';
  if (preference === 'abbreviation' && e.abbreviation !== undefined) return e.abbreviation;
  if (e.label !== undefined) {
    const r = resolveLocalized(e.label, locale, defaultLocale);
    if (r.length > 0) return r;
  }
  if (preference === 'name') return e.name;
  if (e.abbreviation !== undefined) return e.abbreviation;
  return e.name;
}

/**
 * Layout decision: which calendars ship with which patterns. Kept as a
 * tiny table so the format function stays declarative.
 *
 * - `harptos`: `"11 Mirtul, 1492 DR"` — day, space, month, comma, year,
 *   space, era abbreviation. Matches Forgotten Realms canon.
 * - `gregorian`: `"22 May 2026 CE"` — day-month-year-era with era
 *   abbreviation. (Note: not `Intl.DateTimeFormat` — see module header.)
 * - any other calendar: same generic `"D MONTH, Y ERA"` pattern.
 */
function patternFor(calendarId: string): 'd-month-comma-y-era' | 'd-month-y-era' {
  if (calendarId === 'gregorian') return 'd-month-y-era';
  return 'd-month-comma-y-era';
}

export function formatDisplayDate(
  calendar: Calendar,
  date: DisplayDate,
  options: FormatDisplayDateOptions = {},
): string {
  const parts = formatDisplayDateParts(calendar, date, options);
  switch (patternFor(calendar.id)) {
    case 'd-month-y-era':
      return `${parts.day} ${parts.month} ${parts.year} ${parts.era}`.trim();
    case 'd-month-comma-y-era':
      return `${parts.day} ${parts.month}, ${parts.year} ${parts.era}`.trim();
  }
}

export function formatDisplayDateParts(
  calendar: Calendar,
  date: DisplayDate,
  options: FormatDisplayDateOptions = {},
): DisplayDateParts {
  const locale = options.locale ?? 'en';
  const defaultLocale = options.defaultLocale ?? 'en';
  const preference = options.era ?? 'abbreviation';
  const eraFull = findEraFull(calendar, date.era);
  const namedYear = eraFull !== null ? resolveNamedYear(eraFull, date.year) : null;
  return {
    day: String(date.day),
    month: resolveMonthLabel(calendar, date.month, locale, defaultLocale),
    year: namedYear ?? String(date.year),
    era: resolveEraAffix(calendar, date.era, locale, defaultLocale, preference),
  };
}


/**
 * Which display fields a given in-world {@link Precision} renders (ADR-0005 §5).
 * A bound stored as an exact day but MARKED at a coarser precision should read
 * honestly fuzzy — "1063 DR" (year), "Hammer 1063" (month/season), or the full
 * "3 Hammer, 1063 DR" (day or finer). Time-of-day precisions (`hour` and finer)
 * reduce to the full date here — `DisplayDate` carries no seconds, so a
 * `game_date`-shaped validity bound has nothing finer than a day to show.
 */
function precisionShows(precision: Precision): { year: boolean; month: boolean; day: boolean } {
  switch (precision) {
    case 'year':
      return { year: true, month: false, day: false };
    case 'season':
    case 'month':
      return { year: true, month: true, day: false };
    case 'day':
    case 'hour':
    case 'minute':
    case 'second':
      return { year: true, month: true, day: true };
  }
}

/**
 * Format a display date HONORING an editorial precision hint (ADR-0005 §5). The
 * stored day stays exact; this only chooses how COARSELY to render it:
 *
 *   - `year`          → `"1063 DR"`
 *   - `season`/`month`→ `"Hammer 1063 DR"`
 *   - `day` and finer → identical to {@link formatDisplayDate}
 *
 * `precision` undefined renders the full date (the default exact rendering),
 * so existing call sites that never set a precision are unchanged. The output
 * is text only — the colour-blind/a11y signal is the WORDS ("the 1060s"), never
 * a colour.
 */
export function formatDisplayDateAtPrecision(
  calendar: Calendar,
  date: DisplayDate,
  precision: Precision | undefined,
  options: FormatDisplayDateOptions = {},
): string {
  if (precision === undefined) return formatDisplayDate(calendar, date, options);
  const parts = formatDisplayDateParts(calendar, date, options);
  const show = precisionShows(precision);
  const trail = parts.era.length > 0 ? ` ${parts.era}` : '';
  if (!show.month && !show.day) {
    // Year-only: "<year> <era>".
    return `${parts.year}${trail}`.trim();
  }
  if (show.month && !show.day) {
    // Month/season: "<month> <year> <era>".
    return `${parts.month} ${parts.year}${trail}`.trim();
  }
  // Day or finer: identical to the full pattern.
  return formatDisplayDate(calendar, date, options);
}

// ---------- formatter override (06-timeline.md §3, #243) ----------

/**
 * A built-in display formatter (§3, slice C2 / #243): a pure function from
 * a calendar + canonical day to a display string, rendering the canonical
 * day **directly** without going through cycle decomposition. Used for
 * Stardates and 40K Imperial Dating, where a year/month/day breakdown is
 * not meaningful. Deterministic — no `Date`, no I/O, no calendar cycle
 * read; the day count is the only input.
 */
export type BuiltinFormatterFn = (calendar: Calendar, day: number) => string;

const SECONDS = '0123456789';

/**
 * Stardate (Star Trek TNG-style continuous decimal). A monotone affine map
 * from the canonical day count: `base + day * rate`, rendered to one
 * decimal. The constants are conventional, not canonical-from-fiction —
 * the load-bearing property is determinism + monotonicity, not matching
 * any on-screen value. `41000.0` is the TNG-era base; `1000` stardate
 * units span one solar year (≈365.25 days).
 */
const STARDATE_BASE = 41000;
const STARDATE_DAYS_PER_YEAR = 365.25;
function formatStardate(_calendar: Calendar, day: number): string {
  const value = STARDATE_BASE + (day * 1000) / STARDATE_DAYS_PER_YEAR;
  return value.toFixed(1);
}

/**
 * Warhammer 40K Imperial Dating: `"<check> <year-fraction>.M<millennium>"`
 * (e.g. `"0 123.M41"`). Decomposed purely from the day count:
 *
 *   - a `year` = `floor(day / 365.25)` (the Imperial year, axis-relative —
 *     display-only, so a coarse 365.25-day year is fine);
 *   - `millennium` = `floor(year / 1000) + 1` (40th millennium = years
 *     39000–39999 → "M40"); negative years stay in earlier millennia;
 *   - `year-fraction` = the position within the year scaled to `000`–`999`;
 *   - `check` = a single deterministic digit derived from the day so the
 *     field is present and stable (the in-fiction "class" digit; here a
 *     reproducible stand-in, never a randomness source).
 */
function formatImperialDating(_calendar: Calendar, day: number): string {
  const yearLen = 365.25;
  const year = Math.floor(day / yearLen);
  const millennium = Math.floor(year / 1000) + 1;
  const intoYear = day - year * yearLen; // [0, yearLen)
  const fraction = Math.min(999, Math.max(0, Math.floor((intoYear / yearLen) * 1000)));
  const check = SECONDS[mod(day, 10)]!;
  const frac = String(fraction).padStart(3, '0');
  return `${check} ${frac}.M${millennium}`;
}

/**
 * The **closed enum** of built-in display formatters (§3, #243). v1 ships
 * exactly these; arbitrary JS / plugin formatters wait for the Phase-3
 * plugin runtime (slice C1 / #242). A `FormatterRef` whose `name` is not a
 * key here is unknown and degrades (see {@link formatWithFormatterOverride}).
 */
export const BUILTIN_FORMATTERS: Readonly<Record<string, BuiltinFormatterFn>> = Object.freeze({
  stardate: formatStardate,
  imperial_dating: formatImperialDating,
});

/**
 * Resolve a {@link FormatterRef} to its built-in formatter function, or
 * `null` when the ref names an unknown / unsupported formatter (a
 * forward-version name, a typo, or a `plugin`-shaped ref no runtime is yet
 * present for). Pure lookup against the closed {@link BUILTIN_FORMATTERS}
 * enum — never executes arbitrary code.
 */
export function resolveFormatter(ref: FormatterRef): BuiltinFormatterFn | null {
  if (ref.kind !== 'builtin') return null;
  return BUILTIN_FORMATTERS[ref.name] ?? null;
}

/** Does this calendar declare a `formatter_override`? (#243) */
export function hasFormatterOverride(calendar: Calendar): boolean {
  return calendar.formatter_override !== undefined;
}

/**
 * The outcome of attempting a `formatter_override` render (#243):
 *
 *   - `text`         — the display string (override output when used, the
 *                      structured cycle-decomposed output otherwise);
 *   - `usedOverride` — `true` iff a known built-in formatter rendered it;
 *   - `notice`       — `'unknown_formatter'` when an override was declared
 *                      but its ref is unknown, so the caller degraded to the
 *                      structured formatter (the caller surfaces a "this
 *                      calendar uses an unsupported display format" notice).
 */
export interface FormatterOverrideResult {
  text: string;
  usedOverride: boolean;
  notice?: 'unknown_formatter';
}

/**
 * Render a canonical day for **default display** honoring a calendar's
 * `formatter_override` (§3, slice C2 / #243). This is the display
 * chokepoint for override-bearing calendars:
 *
 *   - No override declared → structured render (cycle decomposition via
 *     `toDisplayDate` + {@link formatDisplayDate}); `usedOverride: false`.
 *   - A KNOWN built-in override → renders the canonical day directly,
 *     bypassing cycle decomposition; `usedOverride: true`.
 *   - An UNKNOWN override ref → degrades to the structured render and sets
 *     `notice: 'unknown_formatter'` so the caller can flag it (closed-enum
 *     stance: an unrecognised formatter never executes arbitrary code).
 *
 * The override is **display-only** — this never touches parse (`toCanonicalDay`)
 * or compare; those stay on the calendar's cycles regardless of the override.
 * Pure / deterministic: the canonical day is the only variable input.
 *
 * When the structured fallback itself fails (an undecomposable calendar),
 * the canonical day is rendered as a bare integer so this helper is total.
 */
export function formatWithFormatterOverride(
  calendar: Calendar,
  day: CanonicalDay | number,
  options: FormatDisplayDateOptions = {},
): FormatterOverrideResult {
  const ref = calendar.formatter_override;
  if (ref !== undefined) {
    const fn = resolveFormatter(ref);
    if (fn !== null) return { text: fn(calendar, day), usedOverride: true };
    return { text: structuredOrRaw(calendar, day, options), usedOverride: false, notice: 'unknown_formatter' };
  }
  return { text: structuredOrRaw(calendar, day, options), usedOverride: false };
}

/**
 * Structured (cycle-decomposed) render of a canonical day, falling back to
 * the bare integer when the calendar cannot decompose the day (keeps
 * {@link formatWithFormatterOverride} total).
 */
function structuredOrRaw(calendar: Calendar, day: CanonicalDay | number, options: FormatDisplayDateOptions): string {
  const dd = toDisplayDate(calendar, day);
  if (dd.ok) return formatDisplayDate(calendar, dd.value, options);
  return String(day);
}

// ---------- plugin-calendar graceful degrade (06-timeline.md §3, #242) ----------

/**
 * The outcome of attempting to format a canonical day for a calendar whose
 * mode may be `plugin` (§3, slice C1 / #242).
 *
 *   - `supported: true`  — a structured calendar; `text` is the normal
 *     cycle-decomposed (or `formatter_override`) render.
 *   - `supported: false` — a plugin-mode calendar; NO plugin code runs in
 *     this slice, so there is no display string. `text` is the bare
 *     canonical day count (so a surface that must show *something* stays
 *     total) and `notice: 'plugin_unsupported'` flags that the host should
 *     render the "calendar not fully supported" affordance instead of a
 *     date. Callers key on `supported` to decide whether to degrade.
 */
export interface CalendarDisplayResult {
  text: string;
  supported: boolean;
  notice?: 'plugin_unsupported';
}

/**
 * Format a canonical day for **default display**, degrading gracefully for a
 * plugin-mode calendar (§3, #242). This is the display chokepoint that makes
 * calendar-dependent surfaces (the date picker, formatting) safe in the
 * presence of a plugin calendar the v1 engine cannot render:
 *
 *   - A STRUCTURED runtime `Calendar` renders normally (through any
 *     `formatter_override`, then cycle decomposition); `supported: true`.
 *   - When `calendarIsPlugin` is `true`, the calendar is plugin-mode: this
 *     returns the bare canonical day and `notice: 'plugin_unsupported'`,
 *     WITHOUT touching cycles or running any plugin module. The host shows a
 *     degrade notice rather than a date — no crash, no broken render.
 *
 * `calendarIsPlugin` is passed in (rather than read off `Calendar`) because
 * the runtime `Calendar` type is structured-only by construction — a plugin
 * definition never produces one (`asCalendar` throws). The client resolves
 * the plugin flag from the `CalendarDefinition` via `isPluginCalendar`
 * BEFORE narrowing to a runtime calendar, then threads it here.
 *
 * Pure / deterministic; no plugin code executes (C1 is contract +
 * graceful-degrade only — the Phase-3 runtime owns execution).
 */
export function formatCanonicalDayForDisplay(
  calendar: Calendar | null,
  day: CanonicalDay | number,
  calendarIsPlugin: boolean,
  options: FormatDisplayDateOptions = {},
): CalendarDisplayResult {
  if (calendarIsPlugin || calendar === null) {
    return { text: String(day), supported: false, notice: 'plugin_unsupported' };
  }
  const result = formatWithFormatterOverride(calendar, day, options);
  return { text: result.text, supported: true };
}
